import {
  CrmFileCard,
  CrmVenueImpact,
  CrmIssues,
  CrmResult,
  CrmSummary,
  CrmRowFilters,
  matchesCrmFilter,
  type CrmRowFilter,
} from './CrmWorkflowParts';
import { crmErrorCode, crmErrorText, CrmErrorDetails } from './CrmError';
import { useCrmAdapter } from './CrmWorkflowContext';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CRM_REPLACEMENT_VERSION,
  type MemberCrmReplacementPayload,
  type MemberCrmReplacementPreview,
  type MemberCrmReplacementReceipt,
  type MemberCrmReplacementGroup,
  type MemberOpeningRouting,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import { ALERT, FIELD, NOTE, OUTLINE, PRIMARY } from './importStyles';

export interface Settings {
  venues: { id: string; name: string }[];
  routing: MemberOpeningRouting;
  scopeVenueIds: string[];
  mode: 'LEGACY' | 'REPLACEMENT';
}
const prefix = '/members/import/timed-replacement';
export default function TimedReplacementPanel({ entryVenueId }: { entryVenueId: string }) {
  const { t } = useTranslation('member');
  const actor = useAuthStore((s) => s.user?.id),
    session = useAuthStore((s) => s.sessionRevision);
  const storage = `crm-replacement:${actor}:${entryVenueId}`;
  const [visible, setVisible] = useState(50);
  const [rowFilter, setRowFilter] = useState<CrmRowFilter>({ venue: '', blockedOnly: false });
  const [file, setFile] = useState<File | null>(null),
    [attachments, setAttachments] = useState<File[]>([]);
  const [routing, setRouting] = useState<MemberOpeningRouting>({
    version: 'VENUE_DETAIL_OCT_2026_V1',
    rules: [],
    assignments: [],
  });
  const [venueIds, setVenueIds] = useState<string[]>([]),
    [removed, setRemoved] = useState<MemberCrmReplacementPayload['removedMembers']>([]);
  const [proofs, setProofs] = useState<NonNullable<MemberCrmReplacementPayload['proofs']>>([]);
  const [correctionReason, setCorrectionReason] = useState('');
  const [preview, setPreview] = useState<MemberCrmReplacementPreview | null>(null),
    [receipt, setReceipt] = useState<MemberCrmReplacementReceipt | null>(null);
  const [pending, setPending] = useState<MemberCrmReplacementPayload | null>(() => {
    try {
      const b = JSON.parse(
        sessionStorage.getItem(storage) ?? 'null',
      ) as MemberCrmReplacementPayload | null;
      return b?.version === CRM_REPLACEMENT_VERSION && b.operationKey && b.planHash ? b : null;
    } catch {
      return null;
    }
  });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const [fresh, setFresh] = useState(false);
  const live = useRef(true),
    generation = useRef(0),
    sending = useRef(false),
    initializedSettings = useRef(false);
  const settings = useQuery({
    queryKey: ['crm-replacement-settings', actor, entryVenueId, session],
    queryFn: () => api.get<Settings>(prefix + '/settings'),
    retry: false,
  });
  useEffect(() => {
    if (settings.data && !pending && !initializedSettings.current) {
      initializedSettings.current = true;
      setRouting(settings.data.routing);
      setVenueIds(settings.data.scopeVenueIds);
    }
  }, [settings.data, pending]);
  useEffect(() => {
    live.current = true;
    const activeGeneration = generation.current;
    return () => {
      live.current = false;
      generation.current = activeGeneration + 1;
    };
  }, []);
  useEffect(() => {
    if (!settings.isSuccess || pending) return;
    const key = sessionStorage.getItem(storage + ':recent');
    if (!key) return;
    let active = true;
    void api
      .get<{ state: string; receipt?: MemberCrmReplacementReceipt }>(prefix + '/status', {
        params: { operationKey: key },
      })
      .then((r) => {
        if (active && r.receipt) setReceipt(r.receipt);
      })
      .catch(() => {
        if (active) setReceipt(null);
      });
    return () => {
      active = false;
    };
  }, [settings.isSuccess, storage, pending]);
  const invalidate = (clear = false) => {
    setFresh(false);
    if (clear) setPreview(null);
    setReceipt(null);
    setError('');
  };
  const [technicalError, setTechnicalError] = useState('');
  const fail = (e: unknown) => {
    const code = crmErrorCode(e);
    setTechnicalError(code);
    setError(crmErrorText(t, code));
  };
  const clearRejected = () => {
    setPending(null);
    setFresh(false);
    setReceipt(null);
    planned.current = null;
    sessionStorage.removeItem(storage);
    sessionStorage.removeItem(storage + ':recent');
  };
  const workflow = useCrmAdapter(
    {
      purpose: 'REPLACEMENT',
      phase: receipt
        ? 'RESULT'
        : pending
          ? 'UNKNOWN'
          : preview && fresh
            ? 'CONFIRM'
            : preview
              ? 'PREVIEW'
              : 'FILES',
      dirty: !receipt && (!!file || attachments.length > 0 || !!preview),
      busy,
      unresolved: !!pending,
      canPreview:
        !!file &&
        !pending &&
        !busy &&
        !!settings.data &&
        !!venueIds.length &&
        !routing.rules.some((r) => !r.keyword.trim() || !r.venueId),
      canConfirm: !!preview?.ready && fresh && !pending && !busy && !!settings.data,
    },
    {
      preview: () => {
        void runPreview();
      },
      confirm: () => {
        void confirm();
      },
      back: () => {
        setPreview(null);
        setFresh(false);
      },
    },
  );
  const recover = async () => {
    if (!pending || sending.current) return;
    const workflowToken = workflow.begin('lookup');
    if (!workflowToken) return;
    sending.current = true;
    setBusy(true);
    const g = generation.current;
    try {
      const r = await api.get<{
        state: string;
        receipt?: MemberCrmReplacementReceipt;
        errorCode?: string;
      }>(prefix + '/status', { params: { operationKey: pending.operationKey } });
      if (!live.current || g !== generation.current) return;
      if (r.receipt) {
        setReceipt(r.receipt);
        setPending(null);
        sessionStorage.removeItem(storage);
        sessionStorage.setItem(storage + ':recent', r.receipt.operationKey);
        void settings.refetch();
        setError('');
      } else if (r.state === 'NOT_WRITTEN') {
        clearRejected();
        fail({ response: { data: { code: r.errorCode } } });
      } else setError(t('replacement.recoveryPending'));
    } catch (e) {
      if (live.current && g === generation.current) {
        if (
          (e as { response?: { data?: { outcome?: string } } }).response?.data?.outcome ===
          'NOT_WRITTEN'
        )
          clearRejected();
        fail(e);
      }
    } finally {
      sending.current = false;
      workflow.end(workflowToken);
      if (live.current && g === generation.current) setBusy(false);
    }
  };
  const payload = (): MemberCrmReplacementPayload => ({
    version: CRM_REPLACEMENT_VERSION,
    operationKey: crypto.randomUUID(),
    venueIds,
    routing,
    removedMembers: removed,
    proofs,
    ...(correctionReason.trim() ? { correctionReason } : {}),
  });
  const form = (body: MemberCrmReplacementPayload) => {
    const value = new FormData();
    value.append('report', file!);
    attachments.forEach((f) => value.append('evidence', f));
    value.append('payload', JSON.stringify(body));
    return value;
  };
  const planned = useRef<MemberCrmReplacementPayload | null>(null);
  const runPreview = async () => {
    if (!file || pending || sending.current) return;
    const workflowToken = workflow.begin();
    if (!workflowToken) return;
    sending.current = true;
    setBusy(true);
    setError('');
    const g = generation.current;
    try {
      const b = payload();
      const p = await api.post<MemberCrmReplacementPreview>(prefix + '/preview', form(b), {
        headers: { 'Content-Type': undefined },
        timeout: 65000,
      });
      if (live.current && g === generation.current) {
        planned.current = { ...b, planHash: p.planHash };
        setPreview(p);
        setFresh(true);
        setReceipt(null);
      }
    } catch (e) {
      if (live.current && g === generation.current) fail(e);
    } finally {
      sending.current = false;
      workflow.end(workflowToken);
      if (live.current && g === generation.current) setBusy(false);
    }
  };
  const confirm = async () => {
    const b = pending ?? planned.current;
    if (!b || !file || sending.current || (!pending && (!preview?.ready || !fresh))) return;
    const workflowToken = workflow.begin(pending ? 'original-retry' : 'new');
    if (!workflowToken) return;
    sending.current = true;
    setBusy(true);
    setError('');
    const g = generation.current;
    // Persist the exact original key and payload before dispatch; a lost response never opens a new key.
    try {
      sessionStorage.setItem(storage, JSON.stringify(b));
      setPending(b);
      const r = await api.post<MemberCrmReplacementReceipt>(prefix + '/confirm', form(b), {
        headers: { 'Content-Type': undefined },
        timeout: 65000,
      });
      if (live.current && g === generation.current) {
        setReceipt(r);
        setPending(null);
        setPreview(null);
        sessionStorage.removeItem(storage);
        sessionStorage.setItem(storage + ':recent', r.operationKey);
        void settings.refetch();
      }
    } catch (e) {
      if (live.current && g === generation.current) {
        if (
          (e as { response?: { data?: { outcome?: string } } }).response?.data?.outcome ===
          'NOT_WRITTEN'
        )
          clearRejected();
        fail(e);
      }
    } finally {
      sending.current = false;
      workflow.end(workflowToken);
      if (live.current && g === generation.current) setBusy(false);
    }
  };
  const venue = (id: string) => settings.data?.venues.find((v) => v.id === id)?.name ?? id;
  const hold = (code: string) => crmErrorText(t, code);
  const blocking = preview?.batchHolds.some(
    (code) => !['EVIDENCE_INVALID', 'CORRECTION_REASON_REQUIRED'].includes(code),
  );
  const groupProof = (g: MemberCrmReplacementGroup) =>
    proofs.find(
      (p) =>
        p.venueId === g.venueId && p.ledgerIds.some((id) => g.activity?.some((l) => l.id === id)),
    );
  const updateProof = (
    g: MemberCrmReplacementGroup,
    explanation: string,
    attachment: number,
    overlap: boolean,
  ) => {
    const ids = g.activity!.map((l) => l.id),
      overlapped = g
        .activity!.filter(
          (l) => overlap && l.overlapEligible && l.source === 'TOURNAMENT' && Number(l.delta) > 0,
        )
        .map((l) => l.id);
    const next = proofs.filter((p) => !p.ledgerIds.some((id) => ids.includes(id)));
    if (overlapped.length)
      next.push({
        venueId: g.venueId,
        ledgerIds: overlapped,
        attachment,
        explanation,
        state: 'TOURNAMENT_OVERLAP',
        overlapAmounts: proofs
          .flatMap((p) => p.overlapAmounts ?? [])
          .filter((a) => overlapped.includes(a.ledgerId)),
      });
    const independent = ids.filter((id) => !overlapped.includes(id));
    if (independent.length)
      next.push({
        venueId: g.venueId,
        ledgerIds: independent,
        attachment,
        explanation,
        state: 'INDEPENDENT',
      });
    setProofs(next);
    invalidate();
  };
  return (
    <section className="space-y-4" aria-label={t('replacement.title')}>
      <p className={NOTE}>{t('replacement.guide')}</p>
      {settings.isError && (
        <p role="alert" className={ALERT}>
          {t('replacement.settingsFailed')}
        </p>
      )}
      {receipt && settings.isSuccess && (
        <CrmResult
          receipt={
            <>
              {' '}
              <p className="break-all">
                {t('replacement.receipt')}: {receipt.batchId}
              </p>
              {receipt.revisions.map((r) => (
                <p className="break-all" key={r.venueId}>
                  {venue(r.venueId)}: {r.crmRevision}
                </p>
              ))}
            </>
          }
        >
          <p>{t(receipt.superseded ? 'replacement.superseded' : 'replacement.success')}</p>
          {receipt.summary?.map((v) => (
            <p className="tabular-nums" key={v.venueId}>
              {venue(v.venueId)}: {v.oldContribution} → {v.contribution} ({t('replacement.delta')}:{' '}
              {v.delta})
            </p>
          ))}
          <button
            type="button"
            className={OUTLINE}
            onClick={() => {
              const blob = new Blob([JSON.stringify(receipt, null, 2)], {
                type: 'application/json',
              });
              const url = URL.createObjectURL(blob),
                a = document.createElement('a');
              a.href = url;
              a.download = 'crm-replacement-receipt.json';
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            {t('replacement.download')}
          </button>
        </CrmResult>
      )}
      {pending && (
        <div role="status" className={NOTE}>
          <p>{t('replacement.recovery')}</p>
          <button type="button" className={OUTLINE} disabled={busy} onClick={() => void recover()}>
            {t('replacement.lookup')}
          </button>
          <button
            type="button"
            className={OUTLINE}
            disabled={busy || !file}
            onClick={() => void confirm()}
          >
            {t('replacement.retryOriginal')}
          </button>
        </div>
      )}
      {preview && !receipt && (
        <CrmSummary
          rows={preview.file.rows}
          issues={
            preview.groups.filter((g) => g.holds.length > 0).length +
            preview.batchHolds.length +
            preview.routingIssues.length
          }
          facts={[
            {
              label: t('crmWorkspace.membersCount'),
              value: new Set(preview.groups.flatMap((r) => (r.memberId ? [r.memberId] : []))).size,
            },
            { label: t('crmWorkspace.venuesCount'), value: preview.venues?.length ?? 0 },
            { label: t('crmWorkspace.sourcePeriod'), value: '2026-10' },
          ]}
          ready={preview.ready && fresh}
        />
      )}
      {preview && !receipt && (
        <CrmIssues codes={[...preview.batchHolds, ...preview.groups.flatMap((g) => g.holds)]} />
      )}
      <fieldset disabled={busy || !!pending || !settings.data} className="space-y-3">
        <details>
          <summary className="min-h-11 cursor-pointer">{t('replacement.scope')}</summary>
          {settings.data?.venues.map((v) => (
            <label className="flex min-h-11 items-center gap-2" key={v.id}>
              <input
                type="checkbox"
                checked={venueIds.includes(v.id)}
                onChange={(e) => {
                  invalidate();
                  setVenueIds(
                    e.target.checked ? [...venueIds, v.id] : venueIds.filter((id) => id !== v.id),
                  );
                }}
              />
              {v.name}
            </label>
          ))}
          <p>{t('replacement.mapping')}</p>
          {routing.rules.map((r, i) => (
            <div className="grid gap-2 sm:grid-cols-2" key={i}>
              <input
                aria-label={t('replacement.keyword')}
                className={FIELD}
                value={r.keyword}
                onChange={(e) => {
                  invalidate();
                  setRouting({
                    ...routing,
                    rules: routing.rules.map((v, n) =>
                      n === i ? { ...v, keyword: e.target.value } : v,
                    ),
                  });
                }}
              />
              <select
                aria-label={t('replacement.destination')}
                className={FIELD}
                value={r.venueId}
                onChange={(e) => {
                  invalidate();
                  setRouting({
                    ...routing,
                    rules: routing.rules.map((v, n) =>
                      n === i ? { ...v, venueId: e.target.value } : v,
                    ),
                  });
                }}
              >
                <option value="">{t('replacement.destination')}</option>
                {settings.data?.venues.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={OUTLINE}
                onClick={() => {
                  setRouting({ ...routing, rules: routing.rules.filter((_, n) => n !== i) });
                  invalidate();
                }}
              >
                {t('replacement.deleteRule')}
              </button>
            </div>
          ))}
          <button
            type="button"
            className={OUTLINE}
            onClick={() => {
              invalidate();
              setRouting({ ...routing, rules: [...routing.rules, { keyword: '', venueId: '' }] });
            }}
          >
            {t('replacement.addRule')}
          </button>
        </details>
        <details>
          <summary className="min-h-11 cursor-pointer">{t('replacement.correction')}</summary>
          <input
            aria-label={t('replacement.correction')}
            className={FIELD}
            value={correctionReason}
            onChange={(e) => {
              setCorrectionReason(e.target.value);
              invalidate();
            }}
          />
        </details>
      </fieldset>
      <CrmFileCard>
        <label className="block">
          {t('replacement.file')}
          <input
            type="file"
            data-testid="crm-replacement-report"
            accept=".xlsx"
            disabled={busy}
            className="block min-h-11 max-w-full"
            onChange={(e) => {
              invalidate(true);
              setFile(e.target.files?.[0] ?? null);
              setRouting((r) => ({ ...r, assignments: [] }));
              setRemoved([]);
              setProofs([]);
              setAttachments([]);
            }}
          />
        </label>
      </CrmFileCard>
      {pending && !!pending.proofs?.length && (
        <CrmFileCard>
          <label className="block">
            {t('replacement.originalEvidence')}
            <input
              type="file"
              multiple
              disabled={busy}
              onChange={(e) => setAttachments(Array.from(e.target.files ?? []))}
            />
          </label>
        </CrmFileCard>
      )}
      {!pending && !workflow.embedded && (
        <button
          type="button"
          className={PRIMARY}
          data-testid="crm-replacement-preview"
          disabled={
            busy ||
            !file ||
            !settings.data ||
            !venueIds.length ||
            routing.rules.some((r) => !r.keyword.trim() || !r.venueId)
          }
          onClick={() => void runPreview()}
        >
          {t('replacement.preview')}
        </button>
      )}
      {error && (
        <div role="alert" className={ALERT}>
          {error}
          <CrmErrorDetails code={technicalError} />
        </div>
      )}
      {busy && <p role="status">{t('replacement.working')}</p>}
      {preview && (
        <fieldset disabled={busy || !!pending} className="space-y-4">
          {!fresh && <p className={NOTE}>{t('replacement.stale')}</p>}
          {!!blocking ? (
            <div
              role={preview.noChange ? 'status' : 'alert'}
              className={preview.noChange ? NOTE : ALERT}
            >
              <p className="font-semibold">
                {preview.noChange ? hold('NO_CHANGE') : t('replacement.batchBlocked')}
              </p>
              {[...new Set(preview.batchHolds)]
                .filter((code) => code !== 'NO_CHANGE')
                .map((code) => (
                  <p key={code}>{hold(code)}</p>
                ))}
              {preview.previous.map((v) => (
                <p className="break-all" key={v.venueId}>
                  {venue(v.venueId)} · {t('replacement.baseline')}: {v.legacyBatchId ?? v.revision}
                </p>
              ))}
            </div>
          ) : (
            <>
              {preview.batchHolds.length > 0 && (
                <div role="alert" className={ALERT}>
                  {preview.batchHolds.map((code) => (
                    <p key={code}>{hold(code)}</p>
                  ))}
                </div>
              )}
              {preview.routingIssues.length > 0 && (
                <div className={ALERT}>
                  <p>{t('replacement.routingHold')}</p>
                  {[...new Set(preview.routingIssues.map((r) => r.remark))].map((remark) => (
                    <div key={remark} className="space-y-2">
                      <p className="break-words">{remark || t('replacement.blankRemark')}</p>
                      <select
                        aria-label={t('replacement.destination')}
                        className={FIELD}
                        defaultValue=""
                        onChange={(e) => {
                          const rows = preview.routingIssues
                            .filter((r) => r.remark === remark)
                            .map((r) => r.rowNumber);
                          if (
                            remark.trim() &&
                            preview.routingIssues
                              .filter((r) => r.remark === remark)
                              .every((r) => r.reason === 'UNKNOWN')
                          ) {
                            setRouting({
                              ...routing,
                              rules: [
                                ...routing.rules,
                                { keyword: remark, venueId: e.target.value },
                              ],
                            });
                          } else
                            setRouting({
                              ...routing,
                              assignments: [
                                ...routing.assignments,
                                {
                                  fileSha256: preview.file.sha256,
                                  venueId: e.target.value,
                                  rowNumbers: rows,
                                },
                              ],
                            });
                          invalidate();
                        }}
                      >
                        <option value="">{t('replacement.destination')}</option>
                        {settings.data?.venues.map((v) => (
                          <option value={v.id} key={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              <CrmRowFilters
                value={rowFilter}
                onChange={setRowFilter}
                venues={settings.data?.venues ?? []}
                total={preview.groups.length}
                matched={preview.groups.filter((row) => matchesCrmFilter(row, rowFilter)).length}
                limit={visible}
                onMore={() => setVisible((n) => n + 50)}
              />
              {preview.groups
                .filter((g) => g.holds.length && matchesCrmFilter(g, rowFilter))
                .slice(0, visible)
                .map((g) => (
                  <div className={ALERT} key={`${g.venueId}:${g.memberId}`}>
                    <p>
                      {venue(g.venueId)} · {g.memberLabel ?? g.memberId}
                    </p>
                    {g.holds.map((h) => (
                      <p key={h}>{hold(h)}</p>
                    ))}
                    {g.holds.includes('MEMBER_ABSENT') && (
                      <button
                        type="button"
                        className={OUTLINE}
                        onClick={() => {
                          setRemoved([...removed, { venueId: g.venueId, memberId: g.memberId }]);
                          invalidate();
                        }}
                      >
                        {t('replacement.remove', {
                          before: g.oldContribution,
                          after: g.resultingBalance,
                        })}
                      </button>
                    )}
                    {g.activity && g.activity.length > 0 && (
                      <div className="space-y-2">
                        {g.activity.map((l) => (
                          <p className="break-all" key={l.id}>
                            {t(`ledgerSource_${l.source}`)} {l.delta} · {l.id}
                          </p>
                        ))}
                        <CrmFileCard>
                          <label>
                            {t('replacement.evidence')}
                            <input
                              type="file"
                              disabled={attachments.length >= 4}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) setAttachments([...attachments, f]);
                              }}
                            />
                          </label>
                        </CrmFileCard>
                        <label>
                          {t('replacement.evidenceChoice')}
                          <select
                            className={FIELD}
                            value={groupProof(g)?.attachment ?? Math.max(0, attachments.length - 1)}
                            onChange={(e) =>
                              updateProof(
                                g,
                                groupProof(g)?.explanation ?? '',
                                Number(e.target.value),
                                proofs.some(
                                  (p) =>
                                    p.state === 'TOURNAMENT_OVERLAP' &&
                                    p.ledgerIds.some((id) => g.activity!.some((l) => l.id === id)),
                                ),
                              )
                            }
                          >
                            {attachments.map((f, i) => (
                              <option key={i} value={i}>
                                {i + 1}. {f.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        {g.activity.some(
                          (l) =>
                            l.overlapEligible && l.source === 'TOURNAMENT' && Number(l.delta) > 0,
                        ) && (
                          <label>
                            {t('replacement.proofFinding')}
                            <select
                              className={FIELD}
                              value={
                                proofs.some(
                                  (p) =>
                                    p.state === 'TOURNAMENT_OVERLAP' &&
                                    p.ledgerIds.some((id) => g.activity!.some((l) => l.id === id)),
                                )
                                  ? 'OVERLAP'
                                  : 'INDEPENDENT'
                              }
                              onChange={(e) =>
                                updateProof(
                                  g,
                                  groupProof(g)?.explanation ?? '',
                                  groupProof(g)?.attachment ?? Math.max(0, attachments.length - 1),
                                  e.target.value === 'OVERLAP',
                                )
                              }
                            >
                              <option value="INDEPENDENT">{t('replacement.independent')}</option>
                              <option value="OVERLAP">{t('replacement.tournamentOverlap')}</option>
                            </select>
                          </label>
                        )}
                        {g.activity
                          .filter(
                            (l) =>
                              l.overlapEligible &&
                              proofs.some(
                                (p) =>
                                  p.state === 'TOURNAMENT_OVERLAP' && p.ledgerIds.includes(l.id),
                              ),
                          )
                          .map((l) => (
                            <label className="block" key={l.id}>
                              {t('replacement.overlapAmount', { id: l.id })}
                              <input
                                className={FIELD}
                                inputMode="numeric"
                                maxLength={10}
                                value={
                                  proofs
                                    .flatMap((p) => p.overlapAmounts ?? [])
                                    .find((a) => a.ledgerId === l.id)?.amount ?? l.delta
                                }
                                onChange={(e) => {
                                  setProofs(
                                    proofs.map((p) =>
                                      p.state === 'TOURNAMENT_OVERLAP' && p.ledgerIds.includes(l.id)
                                        ? {
                                            ...p,
                                            overlapAmounts: [
                                              ...(p.overlapAmounts ?? []).filter(
                                                (a) => a.ledgerId !== l.id,
                                              ),
                                              { ledgerId: l.id, amount: e.target.value },
                                            ],
                                          }
                                        : p,
                                    ),
                                  );
                                  invalidate();
                                }}
                              />
                            </label>
                          ))}
                        <label>
                          {t('replacement.explanation')}
                          <textarea
                            className={`${FIELD} h-24`}
                            value={
                              proofs.find(
                                (p) =>
                                  p.venueId === g.venueId &&
                                  p.ledgerIds.some((id) => g.activity!.some((l) => l.id === id)),
                              )?.explanation ?? ''
                            }
                            onChange={(e) => {
                              updateProof(
                                g,
                                e.target.value,
                                groupProof(g)?.attachment ?? Math.max(0, attachments.length - 1),
                                proofs.some(
                                  (p) =>
                                    p.state === 'TOURNAMENT_OVERLAP' &&
                                    p.ledgerIds.some((id) => g.activity!.some((l) => l.id === id)),
                                ),
                              );
                            }}
                          />
                        </label>
                        <p className="text-sm">{t('replacement.proofScope')}</p>
                      </div>
                    )}
                  </div>
                ))}
              <details>
                <summary className="min-h-11 cursor-pointer">
                  {t('replacement.rows', { count: preview.groups.length })}
                </summary>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th>{t('replacement.member')}</th>
                        <th>{t('replacement.old')}</th>
                        <th>{t('replacement.new')}</th>
                        <th>{t('replacement.delta')}</th>
                        <th>{t('replacement.balance')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.groups
                        .filter((row) => matchesCrmFilter(row, rowFilter))
                        .slice(0, visible)
                        .map((g) => (
                          <tr key={`${g.venueId}:${g.memberId}`}>
                            <td className="p-2">
                              {venue(g.venueId)} · {g.memberLabel ?? g.memberId}
                            </td>
                            {[g.oldContribution, g.contribution, g.delta, g.resultingBalance].map(
                              (v, i) => (
                                <td className="p-2 text-right tabular-nums" key={i}>
                                  {v}
                                </td>
                              ),
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </details>
              <CrmVenueImpact
                columns={[
                  t('opening.destination'),
                  t('replacement.old'),
                  t('replacement.new'),
                  t('replacement.delta'),
                ]}
                rows={preview.venues.map((v) => ({
                  key: v.venueId,
                  cells: [venue(v.venueId), v.oldContribution, v.contribution, v.delta],
                }))}
              />
              {preview.venues.map((v) => {
                const groups = preview.groups.filter((g) => g.venueId === v.venueId);
                return (
                  <div className="tabular-nums" key={v.venueId}>
                    <p>{venue(v.venueId)}</p>
                    <p>
                      {t('replacement.memberChanges', {
                        total: groups.length,
                        added: groups.filter(
                          (g) => Number(g.oldContribution) === 0 && Number(g.contribution) > 0,
                        ).length,
                        increased: groups.filter(
                          (g) => Number(g.oldContribution) > 0 && Number(g.delta) > 0,
                        ).length,
                        decreased: groups.filter(
                          (g) => Number(g.contribution) > 0 && Number(g.delta) < 0,
                        ).length,
                        removed: groups.filter(
                          (g) => Number(g.oldContribution) > 0 && Number(g.contribution) === 0,
                        ).length,
                      })}
                    </p>
                  </div>
                );
              })}
              <p className={NOTE}>{t('replacement.confirmNotice')}</p>
              {!workflow.embedded && (
                <button
                  type="button"
                  className={PRIMARY}
                  data-testid="crm-replacement-confirm"
                  disabled={busy || !!pending || !preview.ready || !fresh}
                  onClick={() => void confirm()}
                >
                  {t('replacement.confirm')}
                </button>
              )}
            </>
          )}
        </fieldset>
      )}
    </section>
  );
}
